import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type ExpandedState,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import { type FilteredInvoiceRow } from '~entities/payment/api/get-filtered-invoices';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
  EnrollioTableSearch,
} from '~shared/components/enrollio-table';
import { StudentDetailsCrmLink } from '~shared/components/student-details-crm-link';
import { DataTableColumnHeader } from '~shared/components/table';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { InvoiceExpandedRow } from './invoice-expanded-row';

const columnHelper = createColumnHelper<FilteredInvoiceRow>();

function cellMoney(currencySymbol: string, value: number) {
  return `${currencySymbol}${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export interface InvoicesTableProps {
  data: Array<FilteredInvoiceRow>;
  currencySymbol: string;
  isFetching: boolean;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  rowCount: number;
  sorting: SortingState;
  onSortingChange: Dispatch<SetStateAction<SortingState>>;
}

export function InvoicesTable({
  data,
  currencySymbol,
  isFetching,
  pagination,
  onPaginationChange,
  rowCount,
  sorting,
  onSortingChange,
}: InvoicesTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) =>
          (row.parentName ?? '').trim() || row.studentOrParentName.trim() || '',
        {
          id: 'parent',
          header: (info) => (
            <DataTableColumnHeader
              column={info.column}
              title='Parent'
              className='text-black'
              disableHiding
            />
          ),
          cell: ({ row }) => {
            const r = row.original;
            const name = (r.parentName ?? '').trim();
            if (!r.parentId) {
              return <span className='text-muted-foreground'>—</span>;
            }
            const title = name || 'Parent';
            return (
              <Link
                to='/parents/transactions-table'
                search={{ parentId: r.parentId }}
                className='font-medium text-black underline hover:opacity-80'
              >
                {title}
              </Link>
            );
          },
        },
      ),
      columnHelper.accessor(
        (row) =>
          (row.studentName ?? '').trim() ||
          row.studentOrParentName.trim() ||
          '',
        {
          id: 'student',
          header: (info) => (
            <DataTableColumnHeader
              column={info.column}
              title='Student'
              className='text-black'
              disableHiding
            />
          ),
          cell: ({ row }) => {
            const r = row.original;
            const name = (r.studentName ?? '').trim();
            if (!r.studentId) {
              return <span className='text-muted-foreground'>—</span>;
            }
            const title = name || r.studentOrParentName.trim() || 'Student';
            return (
              <StudentDetailsCrmLink
                studentId={r.studentId}
                className='font-medium text-black underline hover:opacity-80'
              >
                {title}
              </StudentDetailsCrmLink>
            );
          },
        },
      ),
      columnHelper.accessor('dueDate', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Due date'
            className='text-black'
            disableHiding
          />
        ),
        cell: (info) => formatDateOnlySafe(info.getValue(), 'MMM d, yyyy', '—'),
      }),
      columnHelper.accessor('paymentDate', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Paid date'
            className='text-black'
            disableHiding
          />
        ),
        cell: (info) =>
          formatDateOnlySafe(info.getValue(), 'MMM d, yyyy', '—'),
      }),
      columnHelper.accessor('className', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Class / Event'
            className='text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          return (
            <span className='max-w-[280px] whitespace-normal break-words'>
              {info.getValue()}
            </span>
          );
        },
      }),
      columnHelper.accessor('sessionName', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Session'
            className='text-black'
            disableHiding
          />
        ),
        cell: (info) => {
          const v = info.getValue();
          return <div>{v === '' ? '—' : v}</div>;
        },
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Status'
            className='text-black'
            disableSorting
          />
        ),
        cell: (info) => <span className='capitalize'>{info.getValue()}</span>,
      }),
      columnHelper.accessor('invoiceAmount', {
        id: 'invoiceAmount',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Invoice amount'
            className='text-black'
            disableSorting
          />
        ),
        cell: ({ row }) => {
          const v = row.original.invoiceAmount ?? 0;
          return <span>{cellMoney(currencySymbol, Number(v))}</span>;
        },
      }),
      columnHelper.accessor('paidAmount', {
        id: 'paidAmount',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Paid amount'
            className='text-black'
            disableSorting
          />
        ),
        cell: ({ row }) => {
          const v = row.original.paidAmount ?? 0;
          return <span>{cellMoney(currencySymbol, Number(v))}</span>;
        },
      }),
      columnHelper.display({
        id: 'expand',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Payment details'
            className='text-black'
            disableSorting
          />
        ),
        cell: ({ row }) => {
          const paymentList = row.original.payments;
          const n = paymentList?.length ?? 0;
          return (
            <button
              type='button'
              onClick={() => {
                row.toggleExpanded();
              }}
              className='flex items-center gap-2 text-sm font-medium text-primary hover:underline'
            >
              {row.getIsExpanded() ? (
                <ChevronUp className='size-4 shrink-0' />
              ) : (
                <ChevronDown className='size-4 shrink-0' />
              )}
              {n > 0 ? `payment(${n})` : 'Details'}
            </button>
          );
        },
      }),
      columnHelper.display({
        id: 'fundsAvailable',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Funds available By'
            className='text-black'
            disableSorting
          />
        ),
        cell: ({ row }) => {
          const r = row.original;
          const isPaid = r.status.toLowerCase() === 'paid';
          const fundsOn = r.fundsAvailableOn;
          if (!isPaid || !fundsOn) return '—';
          return (
            <div className='flex items-center gap-1.5'>
              <span>{format(new Date(fundsOn), 'MMM d, yyyy')}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    className='size-3.5 shrink-0 text-muted-foreground'
                    aria-label='Funds available estimate info'
                  />
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-xs'>
                  <p>
                    Estimated by Stripe. Actual availability may vary and is
                    determined by Stripe and your bank.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'failureReason',
        header: (info) => (
          <DataTableColumnHeader
            column={info.column}
            title='Failure reason'
            className='text-black'
            disableSorting
          />
        ),
        cell: ({ row }) => {
          const r = row.original;
          const isFailed = r.status.toLowerCase() === 'failed';
          if (!isFailed) return '—';
          const reason = r.failureReason?.trim();
          const code = r.failureCode?.trim();
          let text: string;
          if (reason && code) text = `${reason} (${code})`;
          else if (reason) text = reason;
          else if (code) text = code;
          else text = '—';
          return (
            <span className='max-w-[420px] whitespace-normal break-words'>
              {text}
            </span>
          );
        },
      }),
    ],
    [currencySymbol],
  );

  return (
    <EnrollioTable<FilteredInvoiceRow>
      ariaLabel='Invoices'
      totalLabel='Invoices'
      filterHeader={(table) => (
        <EnrollioTableSearch
          placeholder='Search parents...'
          value={
            (table.getColumn('parent')?.getFilterValue() as
              | string
              | undefined) ?? ''
          }
          onChange={(value) => {
            table.getColumn('parent')?.setFilterValue(value);
          }}
        />
      )}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      isUpdating={isFetching}
      manualPagination
      manualSorting
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      rowCount={rowCount}
      sorting={sorting}
      onSortingChange={onSortingChange}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      expanded={expanded}
      onExpandedChange={setExpanded}
      expandedRow={(row) => (
        <InvoiceExpandedRow
          invoice={row.original}
          currencySymbol={currencySymbol}
        />
      )}
    />
  );
}
